import React, { useEffect, useRef, useState } from 'react'
import { ReceiptText, FileCheck } from 'lucide-react'
import { Receivable } from '@/shared/models/receivableModels'
import { Invoice } from '@/shared/models/invoiceModels'
import Tooltip from '@/shared/v2/shared/components/Tooltip'
import { useTranslations } from 'next-intl'
import { isNil } from 'lodash'

interface BillingDocumentCellProps {
    receivableReference?: Receivable['reference'] | undefined
    invoiceNumber?: Invoice['invoiceNumber'] | undefined
}

const BillingDocumentCell = ({ receivableReference, invoiceNumber }: BillingDocumentCellProps) => {
    const t = useTranslations('root')
    const [isTruncated, setIsTruncated] = useState(false)
    const tooltipRef = useRef<HTMLSpanElement>(null)

    if (isNil(receivableReference) && isNil(invoiceNumber)) return <>-</>

    const displayReceivable = receivableReference ? true : false
    const icon = displayReceivable ? <ReceiptText size={16} /> : <FileCheck size={16} />
    const reference = displayReceivable ? receivableReference : invoiceNumber

    useEffect(() => {
        const checkTruncation = () => {
            if (tooltipRef.current && tooltipRef.current.parentElement) {
                const tooltipWidth = tooltipRef.current.scrollWidth
                const parentWidth = tooltipRef.current.parentElement.clientWidth
                setIsTruncated(tooltipWidth > parentWidth)
            }
        }

        checkTruncation()
        window.addEventListener('resize', checkTruncation)
        return () => window.removeEventListener('resize', checkTruncation)
    }, [])

    return (
        <span className="flex justify-start items-center gap-1 w-full overflow-hidden">
            <span className="flex-shrink-0">
                <Tooltip
                    trigger={icon}
                    content={
                        displayReceivable
                            ? t('components.billingDocumentBadge.receivable')
                            : t('components.billingDocumentBadge.invoice')
                    }
                />
            </span>
            <Tooltip
                data-testid="__billing-document-cell-tooltip__"
                content={reference}
                trigger={
                    <span ref={tooltipRef} className="truncate text-ellipsis">
                        {reference}
                    </span>
                }
                isActive={isTruncated}
            />
        </span>
    )
}

export default BillingDocumentCell
